import {
  ExportAdapterError,
  type CaptureOptions,
  type CaptureResult,
  type CaptureSource,
} from './types'

const EXPORT_ROOT = 'data-pdf-export-root'
const BREAK_SELECTOR = '[data-pdf-section], [data-pdf-block]'
const COMPOSED_PADDING_PX = 24
const COMPOSED_GAP_PX = 24
const COMPOSED_MIN_WIDTH_PX = 640

function backgroundColor(element: HTMLElement, configured?: string): string {
  if (configured) return configured
  const exportPage = getComputedStyle(document.documentElement)
    .getPropertyValue('--ui-color-export-page')
    .trim()
  const elementBackground = getComputedStyle(element).backgroundColor
  return exportPage || elementBackground || 'Canvas'
}

export async function waitForUiFrames(frames = 2): Promise<void> {
  await new Promise<void>((resolve) => {
    let remaining = frames
    const step = (): void => {
      if (remaining <= 0) {
        setTimeout(resolve, 0)
        return
      }
      remaining -= 1
      requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  })
}

async function waitForImages(root: HTMLElement): Promise<void> {
  const pending = [...root.querySelectorAll<HTMLImageElement>('img')]
    .filter((image) => !image.complete)
    .map(
      (image) =>
        new Promise<void>((resolve) => {
          image.addEventListener('load', () => resolve(), { once: true })
          image.addEventListener('error', () => resolve(), { once: true })
        }),
    )
  await Promise.allSettled(pending)
}

function replaceFilterInputs(root: HTMLElement): void {
  for (const node of root.querySelectorAll<HTMLElement>('[data-pdf-filter-input]')) {
    const replacement = document.createElement('div')
    const value = 'value' in node && typeof node.value === 'string' ? node.value.trim() : ''
    const attributeValue = node.getAttribute('value')?.trim() ?? ''
    const placeholder = node.getAttribute('placeholder') ?? ''
    replacement.setAttribute('data-pdf-filter-input-static', '')
    if (!(value || attributeValue)) replacement.setAttribute('data-pdf-filter-placeholder', '')
    replacement.className = node.className
    replacement.textContent = value || attributeValue || placeholder
    node.replaceWith(replacement)
  }
}

function removeIgnoredNodes(root: HTMLElement, selectors: readonly string[]): void {
  const allSelectors = ['[data-pdf-ignore]', ...selectors]
  for (const selector of allSelectors) {
    for (const node of root.querySelectorAll(selector)) node.remove()
  }
}

function exportStyles(): HTMLStyleElement {
  const style = document.createElement('style')
  style.textContent = `
    [${EXPORT_ROOT}], [${EXPORT_ROOT}] * {
      animation: none !important;
      transition: none !important;
      caret-color: transparent !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    [${EXPORT_ROOT}] .min-h-screen { min-height: auto !important; }
    [${EXPORT_ROOT}] .sticky,
    [${EXPORT_ROOT}] [style*='position: sticky'] {
      position: static !important;
      inset: auto !important;
      box-shadow: none !important;
    }
    [${EXPORT_ROOT}] button {
      appearance: none !important;
      -webkit-appearance: none !important;
      background-image: none !important;
      font: inherit !important;
    }
    [${EXPORT_ROOT}] .highcharts-contextbutton,
    [${EXPORT_ROOT}] .highcharts-button,
    [${EXPORT_ROOT}] .highcharts-exporting-group,
    [${EXPORT_ROOT}] .highcharts-a11y-proxy-container { display: none !important; }
    [${EXPORT_ROOT}] .highcharts-container,
    [${EXPORT_ROOT}] .highcharts-root,
    [${EXPORT_ROOT}] .highcharts-root text {
      font-family: var(--ui-font-sans) !important;
    }
    [${EXPORT_ROOT}] [data-pdf-filter-root] {
      min-height: 44px !important;
      height: auto !important;
      align-items: center !important;
      gap: 8px !important;
      box-sizing: border-box !important;
    }
    [${EXPORT_ROOT}] [data-pdf-filter-chip] {
      min-height: 24px !important;
      height: auto !important;
      padding: 4px 8px !important;
      box-sizing: border-box !important;
    }
    [${EXPORT_ROOT}] [data-pdf-filter-view] {
      align-items: center !important;
      line-height: 1.15 !important;
    }
    [${EXPORT_ROOT}] [data-pdf-filter-input-wrap] {
      min-height: 28px !important;
      height: 28px !important;
      display: flex !important;
      align-items: center !important;
    }
    [${EXPORT_ROOT}] [data-pdf-filter-input-wrap] > label {
      min-height: 100% !important;
      display: flex !important;
      align-items: center !important;
    }
    [${EXPORT_ROOT}] [data-pdf-filter-input-static] {
      min-height: 28px !important;
      height: 28px !important;
      display: flex !important;
      align-items: center !important;
      padding-block: 0 !important;
      line-height: 1 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      box-sizing: border-box !important;
    }
    [${EXPORT_ROOT}] [data-pdf-filter-placeholder] { color: var(--ui-color-muted) !important; }
    [${EXPORT_ROOT}] .o-td,
    [${EXPORT_ROOT}] .o-th,
    [${EXPORT_ROOT}] .o-td-total,
    [${EXPORT_ROOT}] .numeric-column { max-width: none !important; min-width: 0 !important; }
    [${EXPORT_ROOT}] .o-td > div,
    [${EXPORT_ROOT}] .o-td-total > div { min-width: 0 !important; }
    [${EXPORT_ROOT}] .line-clamp-1,
    [${EXPORT_ROOT}] .line-clamp-2,
    [${EXPORT_ROOT}] .line-clamp-3,
    [${EXPORT_ROOT}] .text-ellipsis {
      overflow: visible !important;
      text-overflow: clip !important;
      -webkit-line-clamp: unset !important;
      line-clamp: unset !important;
      display: block !important;
      white-space: normal !important;
      max-width: none !important;
      width: 100% !important;
    }
  `
  return style
}

function elements(source: CaptureSource): HTMLElement[] {
  return Array.isArray(source) ? [...source] : [source as HTMLElement]
}

function measuredWidth(element: HTMLElement): number {
  return Math.ceil(element.getBoundingClientRect().width || element.scrollWidth || 0)
}

/**
 * The node html2canvas rasterises. A single source is cloned as-is, so an existing capture's output
 * is byte-for-byte unchanged; several sources are stacked in order inside a synthetic root, which
 * is what makes a split header/body export one continuous page (v3 `createPdfRoot`).
 */
function composeRoot(source: HTMLElement[], fill: string): HTMLElement {
  if (source.length === 1) {
    const only = source[0]
    if (only === undefined) throw new ExportAdapterError('capture-failed', 'No export surface.')
    const single = only.cloneNode(true) as HTMLElement
    Object.assign(single.style, {
      width: `${measuredWidth(only) || 1024}px`,
      maxWidth: 'none',
      minWidth: `${measuredWidth(only) || 1024}px`,
      height: 'auto',
      transform: 'none',
      margin: '0',
    })
    return single
  }

  const composed = document.createElement('div')
  const width = Math.max(...source.map(measuredWidth), COMPOSED_MIN_WIDTH_PX)
  Object.assign(composed.style, {
    width: `${width}px`,
    maxWidth: 'none',
    minWidth: `${width}px`,
    height: 'auto',
    transform: 'none',
    margin: '0',
    padding: `${COMPOSED_PADDING_PX}px`,
    background: fill,
    display: 'flex',
    flexDirection: 'column',
    gap: `${COMPOSED_GAP_PX}px`,
  })
  composed.append(...source.map((element) => element.cloneNode(true)))
  return composed
}

function cloneBreakpoints(root: HTMLElement, canvas: HTMLCanvasElement): number[] {
  const rootRect = root.getBoundingClientRect()
  const rootHeight = root.scrollHeight || rootRect.height || 1
  const ratio = canvas.height / rootHeight
  const offsets = [...root.querySelectorAll(BREAK_SELECTOR)]
    .map((node) => Math.round((node.getBoundingClientRect().top - rootRect.top) * ratio))
    .filter((offset) => offset > 0 && offset < canvas.height)
    .sort((left, right) => left - right)
  return [...new Set(offsets)]
}

function createExportClone(source: CaptureSource, options: CaptureOptions) {
  const host = document.createElement('div')
  const parts = elements(source)
  const first = parts[0]
  if (first === undefined) {
    throw new ExportAdapterError('capture-failed', 'No export surface was supplied.')
  }
  const fill = backgroundColor(first, options.backgroundColor)
  const exportRoot = composeRoot(parts, fill)

  host.setAttribute('data-pdf-export-host', '')
  Object.assign(host.style, {
    position: 'fixed',
    left: '-100000px',
    top: '0',
    zIndex: '-1',
    pointerEvents: 'none',
    background: fill,
    padding: '0',
    margin: '0',
  })
  exportRoot.setAttribute(EXPORT_ROOT, '')
  replaceFilterInputs(exportRoot)
  removeIgnoredNodes(exportRoot, options.ignoreSelectors ?? [])
  host.append(exportStyles(), exportRoot)
  document.body.append(host)
  return { exportRoot, fill, cleanup: () => host.remove() }
}

async function loadCaptureVendor() {
  try {
    return (await import('html2canvas-pro')).default
  } catch (cause) {
    throw new ExportAdapterError(
      'vendor-load-failed',
      'The PDF capture engine could not be loaded.',
      cause,
    )
  }
}

/**
 * Clone, sanitise and rasterise one or more live elements; the offscreen host is always removed.
 *
 * Returns the page-break offsets alongside the canvas because they can only be measured here: the
 * clone is destroyed before this resolves, and it is the clone — not the live DOM — that the canvas
 * pixels correspond to.
 */
export async function captureSurface(
  source: CaptureSource,
  options: CaptureOptions = {},
): Promise<CaptureResult> {
  const { exportRoot, fill, cleanup } = createExportClone(source, options)
  try {
    await waitForUiFrames(1)
    await waitForImages(exportRoot)
    await waitForUiFrames(1)
    const html2canvas = await loadCaptureVendor()
    const canvas = await html2canvas(exportRoot, {
      scale: options.scale ?? Math.min(Math.max(window.devicePixelRatio || 1, 1.25), 1.75),
      useCORS: true,
      backgroundColor: fill,
      windowWidth: Math.max(
        window.innerWidth || 0,
        document.documentElement.clientWidth || 0,
        exportRoot.scrollWidth || 0,
      ),
      windowHeight: Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0),
      scrollX: 0,
      scrollY: 0,
      removeContainer: true,
    })
    return { canvas, breakpoints: cloneBreakpoints(exportRoot, canvas) }
  } catch (cause) {
    if (cause instanceof ExportAdapterError) throw cause
    throw new ExportAdapterError(
      'capture-failed',
      'The export surface could not be captured.',
      cause,
    )
  } finally {
    cleanup()
  }
}

export async function captureElement(
  source: CaptureSource,
  options: CaptureOptions = {},
): Promise<HTMLCanvasElement> {
  return (await captureSurface(source, options)).canvas
}
